<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <div v-else>
            <div class="big-filter-label filter-form-wrapper mb-3">
                <b-row>
                    <b-col cols="12" md="6">
                        <b-form-group label="No Invoice">
                            <b-form-input
                                v-model.trim="filterNoInvoice"
                                name="autoVoidNoInvoiceSearchInput"
                                placeholder="Cari no invoice"
                                @keyup.enter="onSearch"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="6">
                        <b-form-group label="Status">
                            <multiselect
                                v-model="filterStatus"
                                :options="statusOptions"
                                :internal-search="true"
                                :allow-empty="false"
                                deselect-label=""
                                select-label=""
                                label="text"
                                track-by="value"
                                name="autoVoidFilterStatus"
                                placeholder="Semua"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
                <div class="d-flex flex-wrap gap-2">
                    <b-button variant="pgiBtn" name="autoVoidSearchBtn" @click="onSearch">
                        Cari
                    </b-button>
                    <b-button variant="pgiBtn" name="autoVoidResetBtn" @click="onReset">
                        Reset
                    </b-button>
                </div>
            </div>

            <b-table
                id="auto-void-log-table"
                bordered
                hover
                show-empty
                responsive
                :busy="isLoadingLogs"
                :items="items"
                :fields="fields"
                class="mb-3"
            >
                <template v-slot:table-busy>
                    <div class="text-center my-3">
                        <b-spinner small class="mr-2" />
                        Memuat data pengeluaran...
                    </div>
                </template>
                <template v-slot:empty>
                    Tidak ada data auto void yang perlu dikembalikan.
                </template>
                <template v-slot:cell(cabang)="row">
                    <div class="smallText">{{ row.item.cabangName || "-" }}</div>
                    <div class="smallText text-muted">CA: {{ row.item.numberCa || "-" }}</div>
                    <div class="smallText text-muted">EC: {{ row.item.numberEc || "-" }}</div>
                </template>
                <template v-slot:cell(itemType)="row">
                    <div class="smallText">{{ row.item.itemType }}</div>
                    <div class="smallText text-muted">
                        {{ row.item.categoryName }}
                    </div>
                    <div class="smallText text-muted">
                        {{ formatCurrency(row.item.price) }} x {{ row.item.qty }}
                    </div>
                </template>
                <template v-slot:cell(noInvoice)="row">
                    <div class="smallText">{{ row.item.noInvoice || "-" }}</div>
                    <div class="smallText text-muted">{{ row.item.createdBy || "-" }}</div>
                    <div class="smallText text-muted">
                        {{ formatDate(row.item.createdAt) }} {{ formatTime(row.item.createdAt) }}
                    </div>
                </template>
                <template v-slot:cell(total)="row">
                    <div class="smallText text-right font-weight-bold">
                        {{ formatCurrency(row.item.total || (row.item.price * row.item.qty)) }}
                    </div>
                </template>
                <template v-slot:cell(notes)="row">
                    <b-button
                        size="sm"
                        variant="pgiBtn"
                        name="autoVoidCatatanBtn"
                        @click="openNotes(row.item)"
                    >
                        Catatan
                    </b-button>
                </template>
                <template v-slot:cell(status)="row">
                    <div class="text-center">
                        <span v-if="row.item.returned" class="status-box-md status-box-success kas-kecil-status-badge">
                            Sudah Dikembalikan
                        </span>
                        <span v-else class="status-box-md status-box-warning kas-kecil-status-badge">
                            Perlu Dikembalikan
                        </span>
                    </div>
                </template>
                <template v-slot:cell(action)="row">
                    <div class="kas-kecil-action-wrapper">
                        <b-button
                            v-if="hasPhoto(row.item)"
                            size="sm"
                            variant="primary"
                            class="kas-kecil-action-btn"
                            name="autoVoidLihatFotoBtn"
                            @click="onShowPhoto(row.item)"
                        >
                            Lihat Foto
                        </b-button>
                        <b-button
                            v-if="!row.item.returned && hasConfirmReturnPermission"
                            size="sm"
                            variant="warning"
                            class="kas-kecil-action-btn text-white"
                            name="autoVoidConfirmReturnBtn"
                            @click="onConfirmAutoVoidReturn(row.item)"
                        >
                            Konfirmasi Pengembalian
                        </b-button>
                    </div>
                </template>
            </b-table>

            <div class="pagination-wrapper d-flex align-items-center justify-content-between">
                <b-input-group prepend="Tampilkan" class="align-items-center tableShowItem">
                    <b-form-select v-model="show" @change="onLimitChange" :options="optionsLimit">
                        <template v-slot:first>
                            <b-form-select-option :value="null" disabled />
                        </template>
                    </b-form-select>
                    <span class="ml-2">{{ totalViewText }}</span>
                </b-input-group>
                <b-pagination
                    class="mb-0"
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    aria-controls="auto-void-log-table"
                    @input="onPageChange"
                />
            </div>
        </div>

        <ModalKasKecilConfirmAutoVoidReturn
            :isSubmitting="isSubmittingAutoVoidReturn"
            :reason.sync="autoVoidReturnReason"
            @submit="submitConfirmAutoVoidReturn"
        />
        <ModalKasKecilCatatan
            :item="selectedNotesItem"
            :returnInfo="selectedNotesReturnInfo"
        />
        <ModalKasKecilLogPhotoPreview
            modalId="modal-preview-log-kas-kecil-void"
            :title="activeLogPhotoPreview.title"
            :url="activeLogPhotoPreview.url"
            :files="activeLogPhotoPreview.files"
            @switch-photo="setActiveLogPhoto"
        />
    </div>
</template>

<script>
import CONSTANTS from '@/constants'
import ModalKasKecilConfirmAutoVoidReturn from '@/components/modals/ModalKasKecilConfirmAutoVoidReturn'
import ModalKasKecilCatatan from '@/components/modals/ModalKasKecilCatatan'
import ModalKasKecilLogPhotoPreview from '@/components/modals/ModalKasKecilLogPhotoPreview'

export default {
    name: "auto-void-pengembalian",
    components: {
        ModalKasKecilConfirmAutoVoidReturn,
        ModalKasKecilCatatan,
        ModalKasKecilLogPhotoPreview,
    },
    data() {
        return {
            initStatus: 0,
            isLoadingLogs: false,
            isSubmittingAutoVoidReturn: false,
            filterNoInvoice: "",
            filterStatus: { value: null, text: "Semua" },
            statusOptions: [
                { value: null, text: "Semua" },
                { value: 0, text: "Perlu Dikembalikan" },
                { value: 1, text: "Sudah Dikembalikan" },
            ],
            selectedNotesItem: null,
            selectedAutoVoidReturnItem: null,
            autoVoidReturnReason: "",
            activeLogPhotoPreview: { title: "", url: "", files: [] },
            fields: [
                { key: "cabang", label: "Cabang", thClass: "kas-kecil-cabang-col", tdClass: "kas-kecil-cabang-col" },
                { key: "noInvoice", label: "Invoice / Pembuat", thClass: "kas-kecil-invoice-col", tdClass: "kas-kecil-invoice-col" },
                { key: "itemType", label: "Barang/Jasa", thClass: "kas-kecil-item-col", tdClass: "kas-kecil-item-col" },
                { key: "total", label: "Total", thClass: "kas-kecil-total-col", tdClass: "kas-kecil-total-col" },
                { key: "notes", label: "Catatan", thClass: "kas-kecil-notes-col text-center", tdClass: "kas-kecil-notes-col text-center" },
                { key: "status", label: "Status", thClass: "kas-kecil-status-col text-center", tdClass: "kas-kecil-status-col text-center" },
                { key: "action", label: "Aksi", thClass: "kas-kecil-action-col", tdClass: "kas-kecil-action-col" },
            ],
            items: [],
            currentPage: 1,
            show: 10,
            perPage: 10,
            totalRows: 0,
            totalViewText: "0 dari 0 data",
            optionsLimit: CONSTANTS.OPTIONS_LIMIT_PAGINATION,
        }
    },
    computed: {
        hasConfirmReturnPermission() {
            return this.$helper.hasPermissionMenuChildren("MRC")
        },
        selectedNotesReturnInfo() {
            const item = this.selectedNotesItem
            if (!item || !item.returned) return null
            return {
                returnedAt: `${this.formatDate(item.returnedAt)} ${this.formatTime(item.returnedAt)}`,
                returnedBy: item.returnedBy || "-",
                reason: item.returnReason || "",
            }
        },
    },
    methods: {
        async init() {
            try {
                this.initStatus = 0
                await this.getItems()
                this.initStatus = 1
            } catch (error) {
                this.initStatus = -1
            }
        },
        getItems(page = 1) {
            this.currentPage = isNaN(page) ? 1 : page
            this.isLoadingLogs = true

            const params = {
                cabangId: this.$helper.getCabangId(),
                page: this.currentPage - 1,
                size: this.perPage,
            }
            if (!this.$helper.isNullOrEmpty(this.filterNoInvoice)) params.noInvoice = this.filterNoInvoice
            const statusValue = this.filterStatus ? this.filterStatus.value : null
            if (!this.$helper.isNullOrEmpty(this.filterStatus) || this.filterStatus.value == 0) params.autoVoidCheckedStatus = statusValue

            return this.$axios.get("mini-cash/auto-void/", { params })
                .then((response) => {
                    const result = response.data || {}
                    const data = Array.isArray(result.data) ? result.data : (Array.isArray(result.content) ? result.content : [])

                    this.items = data.map(this.mapAutoVoidItem)
                    this.totalRows = Number(
                        result.total !== undefined
                            ? result.total
                            : (result.totalElements !== undefined ? result.totalElements : data.length)
                    )
                    this.updateTotalViewText()
                })
                .catch((error) => {
                    this.$helper.parseError(this, error)
                    throw error
                })
                .finally(() => {
                    this.isLoadingLogs = false
                })
        },
        mapAutoVoidItem(item) {
            return {
                id: item.id,
                cabangName: item.cabang,
                numberCa: item.completedNumberCa,
                numberEc: item.completedNumberEc,
                noInvoice: item.noInvoice,
                createdBy: item.createdBy,
                createdAt: item.createdAt,
                itemType: item.barang,
                categoryName: item.kategori,
                price: item.pricePerQty,
                qty: item.qty,
                total: item.refundAmount,
                notes: item.notesLog,
                voidReason: item.reason,
                invoiceFile: this.normalizePhotoList(item.fotoInvoice),
                receiptFile: [],
                returned: Boolean(item.autoVoidCheckedStatus),
                returnedAt: item.autoVoidCheckedAt,
                returnedBy: item.autoVoidCheckedBy,
                returnReason: item.autoVoidNotes,
                raw: item,
            }
        },
        updateTotalViewText() {
            if (this.totalRows === 0) {
                this.totalViewText = "0 dari 0 data"
                return
            }

            const pg = this.currentPage - 1
            const prev = pg * this.show + 1
            let limit = pg * this.show + this.show
            if (limit > this.totalRows) limit = this.totalRows
            this.totalViewText = `Menampilkan ${prev} - ${limit} dari ${this.totalRows} entri`
        },
        onSearch() {
            this.getItems(1)
        },
        onReset() {
            this.filterNoInvoice = ""
            this.filterStatus = this.statusOptions[0]
            this.getItems(1)
        },
        onLimitChange(newPerPage) {
            this.show = newPerPage
            this.perPage = newPerPage
            this.getItems(1)
        },
        onPageChange(newPage) {
            this.getItems(newPage)
        },
        formatCurrency(value) {
            return `Rp. ${this.$helper.addThousandSeparator(Number(value || 0))}`
        },
        formatDate(value) {
            if (this.$helper.isNullOrEmpty(value)) return "-"
            return this.$helper.changeDateFormat(value)
        },
        formatTime(value) {
            if (this.$helper.isNullOrEmpty(value)) return "-"
            return this.$helper.changeDateFormatGetHour(value)
        },
        openNotes(item) {
            this.selectedNotesItem = item
            this.$bvModal.show("modal-catatan-log-kas-kecil")
        },
        hasPhoto(item) {
            return this.normalizePhotoList(item.invoiceFile).length > 0 || this.normalizePhotoList(item.receiptFile).length > 0
        },
        normalizePhotoList(value) {
            if (Array.isArray(value)) return value.filter((item) => !this.$helper.isNullOrEmpty(item))
            if (this.$helper.isNullOrEmpty(value)) return []
            return [value]
        },
        onShowPhoto(item) {
            const files = this.getLogPhotoFiles(item)
            if (files.length === 0) {
                this.$helper.toastErr(this, "File tidak tersedia.")
                return
            }

            this.activeLogPhotoPreview = {
                title: "Lihat Foto",
                url: files[0].url,
                files,
            }
            this.$bvModal.show("modal-preview-log-kas-kecil-void")
        },
        getLogPhotoFiles(item) {
            const invoiceFiles = this.normalizePhotoList(item.invoiceFile).map((file, index) => ({
                label: `Invoice ${index + 1}`,
                url: this.buildFileUrl(file),
            }))
            const proofFiles = this.normalizePhotoList(item.receiptFile).map((file, index) => ({
                label: `Bukti ${index + 1}`,
                url: this.buildFileUrl(file),
            }))

            return invoiceFiles.concat(proofFiles).filter((file) => !this.$helper.isNullOrEmpty(file.url))
        },
        buildFileUrl(path) {
            if (this.$helper.isNullOrEmpty(path)) return ""
            if (/^https?:\/\//.test(path)) return path

            const basePath = String(this.$baseFilePathAws || "").replace(/\/$/, "")
            const filePath = String(path).replace(/^\//, "")
            return `${basePath}/${filePath}`
        },
        setActiveLogPhoto(file) {
            this.activeLogPhotoPreview.url = file.url
        },
        onConfirmAutoVoidReturn(item) {
            this.selectedAutoVoidReturnItem = item
            this.autoVoidReturnReason = ""
            this.$bvModal.show("modal-confirm-auto-void-return-kas-kecil")
        },
        submitConfirmAutoVoidReturn() {
            if (!this.selectedAutoVoidReturnItem || !this.selectedAutoVoidReturnItem.id) {
                this.$helper.toastErr(this, "ID log kas kecil tidak ditemukan.")
                return
            }

            if (this.autoVoidReturnReason.length > 255) {
                this.$helper.toastErr(this, "Keterangan maksimal 255 karakter.")
                return
            }

            this.isSubmittingAutoVoidReturn = true

            const id = this.selectedAutoVoidReturnItem.id
            this.$axios.post(`mini-cash/auto-void/${id}/confirm-refund`, {}, {
                params: { notes: this.autoVoidReturnReason },
            }).then((response) => {
                const result = response.data || {}
                this.$bvModal.hide("modal-confirm-auto-void-return-kas-kecil")
                this.$helper.toastSucc(this, result.name || "Pengembalian dana auto void berhasil dikonfirmasi.")
                this.selectedAutoVoidReturnItem = null
                this.autoVoidReturnReason = ""
                this.$emit("refunded")
                return this.getItems(this.currentPage)
            }).catch((error) => {
                this.$helper.parseError(this, error)
            }).finally(() => {
                this.isSubmittingAutoVoidReturn = false
            })
        },
    },
    created() {
        this.init()
    },
}
</script>

<style scoped>
.kas-kecil-status-badge {
    display: block;
    width: 100%;
    text-align: center;
}

.kas-kecil-action-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

::v-deep .table td {
    padding-bottom: 10px;
    padding-top: 10px;
    vertical-align: top;
}

::v-deep .kas-kecil-cabang-col {
    white-space: nowrap;
    width: 1%;
}

::v-deep .kas-kecil-invoice-col {
    width: 20%;
}

::v-deep .kas-kecil-item-col {
    width: 16%;
}

::v-deep .kas-kecil-total-col {
    width: 10%;
}

::v-deep .kas-kecil-notes-col {
    width: 1%;
    white-space: nowrap;
}

::v-deep .kas-kecil-status-col {
    width: 15%;
}

::v-deep .kas-kecil-action-col {
    width: 22%;
}

@media (max-width: 575.98px) {
    .kas-kecil-action-wrapper {
        flex-direction: column;
        align-items: stretch;
    }
}
</style>
